import apiClient from './apiClient';
import type {
  Profile,
  ProfileCreatePayload,
  ProfileDuplicatePayload,
  ProfileExport,
  ProfileImportPayload,
  ProfileUpdatePayload,
  ProfileUser,
} from '../types/profiles';

const PROFILES_URL = '/profiles';
const ADMIN_USERS_URL = '/admin/users';

export const profileService = {
  list: async (): Promise<Profile[]> => {
    const { data } = await apiClient.get<Profile[]>(`${PROFILES_URL}/`);
    return data;
  },

  get: async (id: string): Promise<Profile> => {
    const { data } = await apiClient.get<Profile>(`${PROFILES_URL}/${id}`);
    return data;
  },

  create: async (payload: ProfileCreatePayload): Promise<Profile> => {
    const { data } = await apiClient.post<Profile>(`${PROFILES_URL}/`, payload);
    return data;
  },

  update: async (id: string, payload: ProfileUpdatePayload): Promise<Profile> => {
    const { data } = await apiClient.put<Profile>(`${PROFILES_URL}/${id}`, payload);
    return data;
  },

  /** Copy a profile (system ones included) to customise it. */
  duplicate: async (id: string, payload: ProfileDuplicatePayload): Promise<Profile> => {
    const { data } = await apiClient.post<Profile>(`${PROFILES_URL}/${id}/duplicate`, payload);
    return data;
  },

  /** Portable JSON of a profile, without ids, scope, role default nor users. */
  exportJson: async (id: string): Promise<ProfileExport> => {
    const { data } = await apiClient.get<ProfileExport>(`${PROFILES_URL}/${id}/export`);
    return data;
  },

  /** Create a profile from an export; the server suffixes the key if it is taken. */
  importJson: async (payload: ProfileImportPayload): Promise<Profile> => {
    const { data } = await apiClient.post<Profile>(`${PROFILES_URL}/import`, payload);
    return data;
  },

  /** Users holding the profile (own org; super-admins may pass an organization). */
  users: async (id: string, organizationId?: string | null): Promise<ProfileUser[]> => {
    const params = organizationId ? { organization_id: organizationId } : undefined;
    const { data } = await apiClient.get<ProfileUser[]>(`${PROFILES_URL}/${id}/users`, { params });
    return data;
  },

  remove: async (id: string, reassignTo?: string | null): Promise<void> => {
    const params = reassignTo ? { reassign_to: reassignTo } : undefined;
    await apiClient.delete(`${PROFILES_URL}/${id}`, { params });
  },

  assignToUser: async (userId: string, profileId: string | null): Promise<Profile | null> => {
    const { data } = await apiClient.patch<Profile | null>(
      `${ADMIN_USERS_URL}/${userId}/profile`,
      { profile_id: profileId }
    );
    return data;
  },
};

export default profileService;
